import { useRef, useState } from "react";
import { apiRequest } from "../../services/api";
import SisRectificationBasis from "./SisRectificationBasis";
import SisRectificationReversal from "./SisRectificationReversal";
import SisPaidRectificationSummary from './SisPaidRectificationSummary';

export default function SisVarianceDecision({
  review,
  base,
  accessToken,
  allowed,
  canReverse,
  onSaved,
  onPosted,
}) {
  const [decision, setDecision] = useState("DISPUTE");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [proposal, setProposal] = useState(null);
  const [posted, setPosted] = useState(review.adjustment || null);
  const [postChecks, setPostChecks] = useState({});
  const postRequest = useRef(null);
  const request = useRef(null),
    pending = useRef(false);
  function edit(setter, value) {
    setter(value);
    request.current = null;
    setError("");
  }
  async function save() {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    request.current ||= window.crypto.randomUUID();
    try {
      const result = await apiRequest(
        `${base}/invoices/variance-reviews/${review.id}/decision`,
        {
          method: "POST",
          accessToken,
          body: {
            requestKey: request.current,
            decision,
            reason,
            noFinancialPostingConfirmed: confirmed,
            noItcClaimConfirmed: confirmed,
          },
        },
      );
      onSaved(result);
    } catch (e) {
      setError(e.message);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  async function previewAdjustment() {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    setProposal(null);
    setPostChecks({});
    postRequest.current = null;
    try {
      const result = await apiRequest(
        `${base}/invoices/variance-reviews/${review.id}/adjustment-preview`,
        {
          method: "POST",
          accessToken,
          body: {
            date: new Date(Date.now() + 19800000).toISOString().slice(0, 10),
          },
        },
      );
      setProposal(result);
    } catch (e) {
      setError(e.message);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  async function postAdjustment() {
    if (pending.current || !proposal?.executionEnabled) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      if (!window.crypto?.randomUUID)
        throw Error("Secure UUID support required");
      postRequest.current ||= window.crypto.randomUUID();
      const result = await apiRequest(
        `${base}/invoices/variance-reviews/${review.id}/adjustment`,
        {
          method: "POST",
          accessToken,
          body: {
            date: proposal.snapshot.date,
            previewHash: proposal.previewHash,
            requestKey: postRequest.current,
            supplierAdjustmentConfirmed: postChecks.supplier === true,
            noItcClaimConfirmed: postChecks.tax === true,
            lifecycleRestrictionAccepted: postChecks.restriction === true,
          },
        },
      );
      setPosted(result);
      setProposal(null);
      onPosted?.(result);
    } catch (e) {
      setError(e.message);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  if (posted)
    return (
      <div className="rounded border border-green-300 p-3 text-sm">
        <SisPaidRectificationSummary snapshot={posted.snapshot}/>
        <div role="status">
          <p className="font-semibold">
            {posted.reversal
              ? "RECTIFICATION_REVERSED"
              : "FINANCIALLY_POSTED_NO_ITC"}
          </p>
          <p>
            Historical supplier adjustment ₹{posted.amount} · Voucher{" "}
            {posted.voucherId}
          </p>
          <p>
            Actual invoice: {posted.invoiceId}. Reload the lifecycle panel to
            refresh invoice history.
          </p>
          <p>
            No input GST claimed or money transferred. Generic reversal remains
            blocked.
          </p>
        </div>
        {!posted.reversal && (
          <SisRectificationBasis
            key={posted.id}
            adjustmentId={posted.id}
            base={base}
            accessToken={accessToken}
            allowed={allowed}
          />
        )}
        <SisRectificationReversal
          key={`${posted.id}:${base}`}
          adjustment={posted}
          base={base}
          accessToken={accessToken}
          allowed={allowed && canReverse}
          onReversed={(r) => {
            const updated = { ...posted, reversal: r };
            setPosted(updated);
            onPosted?.(updated);
          }}
        />
      </div>
    );
  if (review.decision)
    return (
      <div className="space-y-3 rounded-lg bg-gray-50 p-3 text-sm">
        <p>{review.status}</p>
        <p>{review.decision.reason}</p>
        <p>
          The commercial decision alone creates no financial entry or input GST
          claim. An accepted difference needs a separately confirmed posting.
        </p>
        {review.status === "ACCEPTED_PENDING_FINANCIAL_POSTING" && (
          <button
            type="button"
            disabled={busy}
            onClick={previewAdjustment}
            className="rounded-lg border px-3 py-2"
          >
            {busy
              ? "Loading adjustment…"
              : "Preview financial adjustment — no posting"}
          </button>
        )}
        {error && (
          <p role="alert" className="text-red-700">
            {error}
          </p>
        )}
        {proposal && <SisPaidRectificationSummary snapshot={proposal.snapshot}/>}
        {proposal && (
          <section className="space-y-2 rounded border border-amber-300 p-3">
            <h4 className="font-semibold">
              Proposed gross-cost / supplier-payable adjustment
            </h4>
            <p>
              Original due ₹{proposal.snapshot.originalSupplierDue} · Invoice ₹
              {proposal.snapshot.reviewedInvoiceGross} · Signed adjustment ₹
              {proposal.snapshot.signedAdjustment}
            </p>
            <table className="w-full text-left">
              <thead>
                <tr>
                  <th>Ledger</th>
                  <th>Debit</th>
                  <th>Credit</th>
                </tr>
              </thead>
              <tbody>
                {proposal.snapshot.lines.map((line, i) => (
                  <tr key={i}>
                    <td>
                      {proposal.snapshot.ledgerNames?.[line.ledgerId] ||
                        line.ledgerId}
                    </td>
                    <td>₹{line.debit}</td>
                    <td>₹{line.credit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p>
              Proposed {proposal.snapshot.proposedBill.kind} · ₹
              {proposal.snapshot.proposedBill.amount} ·{" "}
              {proposal.snapshot.proposedBill.direction}
              {proposal.snapshot.proposedBill.requiresExplicitAllocation
                ? " — separate reviewed allocation required"
                : ""}
            </p>
            <p className="text-amber-900">{proposal.warning}</p>
            {allowed && proposal.executionEnabled ? (
              <fieldset disabled={busy} className="space-y-2">
                {[
                  [
                    "supplier",
                    "I reviewed the exact supplier due and expense adjustment.",
                  ],
                  ["tax", "No input GST claim or payment is authorised."],
                  [
                    "restriction",
                    "I understand physical returns require reviewed product allocation; corrections and generic reversal remain blocked.",
                  ],
                ].map(([key, label]) => (
                  <label key={key} className="block">
                    <input
                      type="checkbox"
                      checked={!!postChecks[key]}
                      onChange={(e) =>
                        setPostChecks((c) => ({
                          ...c,
                          [key]: e.target.checked,
                        }))
                      }
                    />
                    {label}
                  </label>
                ))}
                <button
                  type="button"
                  disabled={
                    busy ||
                    !postChecks.supplier ||
                    !postChecks.tax ||
                    !postChecks.restriction
                  }
                  onClick={postAdjustment}
                  className="rounded bg-primary-700 px-3 py-2 text-white disabled:opacity-40"
                >
                  Post accepted supplier adjustment
                </button>
              </fieldset>
            ) : (
              <p className="font-semibold">
                Posting disabled. No input GST claim or payment.
              </p>
            )}
          </section>
        )}
      </div>
    );
  if (!allowed)
    return (
      <p className="text-sm text-gray-500">
        Unresolved — decision requires accounting posting permission.
      </p>
    );
  return (
    <fieldset disabled={busy} className="mt-3 space-y-3 rounded-lg border p-3">
      <legend className="px-1 text-sm font-semibold">
        Invoice {review.snapshot.invoiceNumber}: commercial decision
      </legend>
      <label className="block text-sm">
        Decision
        <select
          className="mt-1 w-full rounded border p-2"
          value={decision}
          onChange={(e) => {
            edit(setDecision, e.target.value);
            setConfirmed(false);
          }}
        >
          <option value="DISPUTE">Dispute difference — no posting</option>
          <option value="ACCEPT">
            Rectify incorrect dues — financial posting pending
          </option>
        </select>
      </label>
      <label className="block text-sm">
        Decision reason
        <textarea
          className="mt-1 w-full rounded border p-2"
          value={reason}
          maxLength={1000}
          onChange={(e) => edit(setReason, e.target.value)}
        />
      </label>
      <label className="block text-sm">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => edit(setConfirmed, e.target.checked)}
        />{" "}
        I understand this decision does not change supplier dues, post an
        adjustment or claim input GST.
      </label>
      <p className="text-sm text-amber-800">
        This decision is retained permanently. A changed source needs fresh
        review; a changed decision needs a separate supersession workflow.
      </p>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      <button
        type="button"
        className="rounded-lg bg-primary-700 px-3 py-2 text-white disabled:opacity-40"
        disabled={busy || !confirmed || reason.trim().length < 20}
        onClick={save}
      >
        {busy ? "Saving decision…" : "Retain commercial decision"}
      </button>
    </fieldset>
  );
}
